import { useEffect, useState } from "react";
import SEOHead from "@/components/SEOHead";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowRight } from "lucide-react";
import Logo from "@/components/Logo";
import AccompagnementFormModal from "@/components/AccompagnementFormModal";

const stages = [
  {
    stage: "Stade 1",
    range: "0 – 15 K$ / mois",
    title: "Vous ne générez pas de demande. Pas par manque de talent.",
    body: "Vous livrez un excellent produit ou service, mais votre croissance dépend du bouche-à-oreille. Chaque nouveau client arrive par hasard. Vous n'avez aucun moyen de prévoir le mois prochain, donc vous n'investissez pas.",
    bottleneck: "Aucun canal d'acquisition contrôlable.",
  },
  {
    stage: "Stade 2",
    range: "15 – 50 K$ / mois",
    title: "Vous générez des leads. Mais c'est vous, le système.",
    body: "Les références rentrent, la publicité fonctionne par vagues, le contenu génère de l'attention. Rien n'est documenté ni relié. Chaque résultat dépend de votre présence, de votre suivi et de votre énergie.",
    bottleneck: "La croissance repose sur vous, pas sur une infrastructure.",
  },
  {
    stage: "Stade 3",
    range: "50 K$ + / mois",
    title: "Vous avez du volume. Vous perdez la rentabilité.",
    body: "Les campagnes tournent, l'équipe grossit, les coûts d'acquisition montent. Vous ne savez plus quel canal génère réellement du chiffre d'affaires, alors vous augmentez les budgets pour maintenir le rythme.",
    bottleneck: "Des canaux qui ne se parlent pas et des données non exploitées.",
  },
];

const systemSteps = [
  { label: "Contenu", note: "Attention" },
  { label: "Acquisition", note: "Portée" },
  { label: "Conversion", note: "Intérêt" },
  { label: "Suivi", note: "Relance" },
  { label: "Ventes", note: "Revenus" },
  { label: "Données", note: "Décisions" },
];

const roleLines = [
  { title: "Créer la demande", desc: "Attirer les bonnes personnes." },
  { title: "Transformer la demande", desc: "Convertir l'intérêt en opportunités réelles." },
  { title: "Optimiser la croissance", desc: "Mesurer ce qui génère réellement des ventes." },
];

const partnerCriteria = ["Une offre éprouvée", "Une ambition de croissance", "Une volonté d'investir dans le système"];

const method = [
  {
    num: "01",
    title: "Comprendre",
    desc: "Identifier où se trouvent les plus grandes opportunités de croissance dans l'écosystème actuel.",
  },
  {
    num: "02",
    title: "Construire",
    desc: "Connecter les bonnes stratégies, campagnes, contenus et systèmes autour du parcours client.",
  },
  {
    num: "03",
    title: "Optimiser",
    desc: "Utiliser les données pour améliorer continuellement l'acquisition et la conversion.",
  },
];

const faqs = [
  {
    q: "Est-ce que vous garantissez des résultats?",
    a: "Nous garantissons un système construit, mesuré et optimisé chaque semaine. Nous nous engageons sur des indicateurs précis (volume de leads, coût par lead, rendez-vous générés) et vous voyez les chiffres en continu. Aucune agence sérieuse ne peut garantir vos ventes sans contrôler votre processus de closing.",
  },
  {
    q: "En combien de temps voit-on des résultats?",
    a: "Les premiers leads qualifiés arrivent généralement dans les premières semaines. La stabilité du système, elle, se construit sur quelques mois : c'est le temps nécessaire pour accumuler assez de données et optimiser.",
  },
  {
    q: "Est-ce que ça remplace mon équipe marketing?",
    a: "Non. Nous devenons la structure de croissance autour de votre équipe. Si vous avez déjà des ressources internes, nous leur donnons un cadre, des priorités et des données. Si vous n'en avez pas, nous jouons ce rôle.",
  },
  {
    q: "Nous avons déjà essayé la publicité sans succès.",
    a: "C'est le cas le plus fréquent. La publicité échoue rarement à cause de la publicité : l'offre n'est pas assez claire, la page ne convertit pas, le suivi des leads est inexistant. Nous corrigeons le système avant de remettre du budget média.",
  },
  {
    q: "Quel investissement faut-il prévoir?",
    a: "L'accompagnement s'adresse à des entreprises capables d'investir de façon soutenue dans leur croissance, budget média inclus. Le montant exact dépend de vos objectifs et de votre marché; nous en discutons ouvertement dès la première conversation.",
  },
  {
    q: "Que se passe-t-il après avoir rempli le formulaire?",
    a: "Nous analysons votre situation et nous vous appelons si nous croyons pouvoir avoir un réel impact. Pas de séquence automatisée, pas d'appel de vente déguisé : une conversation directe sur votre croissance.",
  },
];

const Eyebrow = ({ children }: { children: React.ReactNode }) => (
  <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-primary md:text-[11px]">{children}</p>
);

const reveal = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] },
};

const lineDraw = {
  hidden: { scaleX: 0, scaleY: 0 },
  show: { scaleX: 1, scaleY: 1, transition: { duration: 1.6, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] } },
};

const StagesTimeline = () => (
  <motion.div
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, margin: "-100px" }}
    className="relative mt-14 md:mt-20"
  >
    {/* Ligne de progression — horizontale en desktop */}
    <div aria-hidden className="absolute left-0 right-0 top-[6px] hidden h-[2px] md:block">
      <motion.div
        variants={lineDraw}
        className="h-full w-full origin-left rounded-full bg-gradient-to-r from-primary/25 via-primary/60 to-primary"
      />
    </div>
    {/* Ligne de progression — verticale en mobile */}
    <div aria-hidden className="absolute bottom-2 left-[3px] top-2 w-[2px] md:hidden">
      <motion.div
        variants={lineDraw}
        className="h-full w-full origin-top rounded-full bg-gradient-to-b from-primary/25 via-primary/60 to-primary"
      />
    </div>


    <div className="grid gap-14 md:grid-cols-3 md:gap-10">
      {stages.map((s, i) => (
        <motion.div
          key={s.stage}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ delay: 0.14 * i, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="relative pl-8 md:pl-0 md:pr-6"
        >
          <span
            aria-hidden
            className="absolute left-0 top-1.5 h-[7px] w-[7px] rounded-full bg-primary md:relative md:left-auto md:top-auto md:mb-10 md:block"
            style={{ opacity: 0.5 + i * 0.25 }}
          />
          <div className="flex items-baseline gap-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-primary">{s.stage}</p>
            <span aria-hidden className="h-px flex-1 bg-border/70 md:hidden" />
          </div>
          <p className="mt-3 font-heading text-[clamp(1.3rem,3.4vw,1.8rem)] font-semibold leading-none tracking-tight text-foreground">
            {s.range}
          </p>
          <h3 className="mt-6 font-heading text-lg font-medium leading-snug tracking-tight text-foreground md:text-xl">
            {s.title}
          </h3>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground md:text-base">{s.body}</p>
          <div className="mt-6 border-t border-border/70 pt-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground/60">
              Le vrai goulot
            </p>
            <p className="mt-2 text-sm leading-relaxed text-foreground md:text-base">{s.bottleneck}</p>
          </div>
        </motion.div>
      ))}
    </div>
  </motion.div>

);

const Accompagnement = () => {
  const [formOpen, setFormOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const openForm = () => setFormOpen(true);

  return (
    <>
      <SEOHead
        title="Accompagnement marketing stratégique | Génération de leads B2B – Agence 8020"
        description="Accompagnement marketing pour entreprises qui veulent bâtir un système de croissance : stratégie, contenu, acquisition, conversion et données."
        canonical="/accompagnement"
      />

      <AccompagnementFormModal open={formOpen} onOpenChange={setFormOpen} />

      <div className="min-h-screen bg-background text-foreground">
        {/* Header */}
        <header className="fixed inset-x-0 top-0 z-40 border-b border-border/60 bg-background/80 backdrop-blur-xl">
          <div className="mx-auto flex max-w-[1280px] items-center justify-between px-6 py-4 md:px-8">
            <Logo />
            <Button
              size="sm"
              variant="ghost"
              onClick={openForm}
              className="rounded-full border border-border px-4 text-xs font-medium text-foreground hover:bg-secondary md:text-sm"
            >
              Nous parler
            </Button>
          </div>
        </header>

        {/* Hero + VSL — tient dans un seul écran */}
        <section className="flex min-h-[100svh] flex-col justify-center px-6 pb-[clamp(1rem,3vh,2rem)] pt-[clamp(4.5rem,10vh,6rem)] md:px-8">
          <div className="mx-auto w-full max-w-[1000px] text-center">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            >
              <Eyebrow>Partenaire de croissance</Eyebrow>
              <h1 className="mx-auto mt-[clamp(0.6rem,1.6vh,1.25rem)] max-w-[900px] font-heading text-[clamp(1.6rem,4vw,3.1rem)] font-semibold leading-[1.05] tracking-tight">
                Nous construisons des systèmes de croissance.
                <span className="block font-heading italic text-primary">Pas des livrables.</span>
              </h1>
              <p className="mx-auto mt-[clamp(0.5rem,1.4vh,1rem)] max-w-xl text-sm leading-relaxed text-muted-foreground">
                Stratégie, contenu, acquisition, conversion et données réunis dans un seul système.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="mx-auto mt-[clamp(0.75rem,2.2vh,1.75rem)] w-full"
              style={{ maxWidth: "min(100%, calc(min(40svh, 460px) * 16 / 9))" }}
            >
              <div className="aspect-video w-full overflow-hidden rounded-2xl border border-border bg-black">
                <iframe
                  src="https://player.vimeo.com/video/1219231031?title=0&byline=0&portrait=0&dnt=1&transparent=0"
                  title="Présentation de l'accompagnement stratégique - Agence 8020"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  loading="lazy"
                  style={{ backgroundColor: "#000", border: 0 }}
                  className="block h-full w-full"
                />
              </div>
            </motion.div>


            <div className="mt-[clamp(0.75rem,2.2vh,1.5rem)] flex flex-col items-center gap-2">
              <Button
                onClick={openForm}
                className="group h-auto rounded-full bg-primary px-8 py-4 text-base font-semibold text-primary-foreground hover:bg-primary/90"
              >
                Présenter mon entreprise
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
              <p className="text-xs text-muted-foreground/80 md:text-sm">Une conversation, pas un appel de vente.</p>
            </div>
          </div>
        </section>



        {/* 01 — Le constat */}
        <section className="border-t border-border/60 px-6 py-20 md:px-8 md:py-28">
          <motion.div {...reveal} className="mx-auto max-w-[1280px]">
            <Eyebrow>Le constat</Eyebrow>
            <h2 className="mt-6 max-w-4xl font-heading text-[clamp(1.6rem,4vw,2.6rem)] font-semibold leading-[1.08] tracking-tight">
              Plus de marketing ne règle pas
              <span className="block text-muted-foreground">un système qui ne fonctionne pas ensemble.</span>
            </h2>
            <div className="mt-10 grid gap-8 md:mt-14 md:grid-cols-[1fr_1fr] md:gap-20">
              <p className="text-base leading-relaxed text-muted-foreground md:text-[1.0625rem]">
                Votre contenu, vos campagnes, vos leads, votre CRM et votre équipe de vente peuvent tous fonctionner{" "}
                <span className="text-foreground">individuellement</span>.
              </p>
              <p className="text-base leading-relaxed text-muted-foreground md:text-[1.0625rem]">
                La croissance devient <span className="text-primary">prévisible</span> lorsqu'ils travaillent{" "}
                <span className="text-foreground">ensemble</span>.
              </p>
            </div>
          </motion.div>
        </section>

        {/* 02 — Trois stades */}
        <section className="border-t border-border/60 px-6 py-20 md:px-8 md:py-28">
          <div className="mx-auto max-w-[1280px]">
            <motion.div {...reveal}>
              <Eyebrow>Où vous en êtes</Eyebrow>
              <h2 className="mt-6 max-w-4xl font-heading text-[clamp(1.6rem,4vw,2.6rem)] font-semibold leading-[1.08] tracking-tight">
                Trois stades.
                <span className="block text-muted-foreground">Le même problème.</span>
              </h2>
              <p className="mt-8 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-[1.0625rem]">
                Que vous démarriez ou que vous fassiez sept chiffres, la douleur change de forme mais la racine reste la
                même : votre marketing n'est pas un système.
              </p>
            </motion.div>

            <StagesTimeline />

            <motion.p {...reveal} className="mt-14 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-[1.0625rem]">
              Chaque stade est un mur différent. La racine est identique.
              <span className="block text-foreground">C'est exactement ce que nous construisons avec vous.</span>
            </motion.p>
          </div>
        </section>

        {/* 03 — Le système 8020 */}
        <section className="border-t border-border/60 px-6 py-20 md:px-8 md:py-28">
          <div className="mx-auto max-w-[1280px]">
            <motion.div {...reveal}>
              <Eyebrow>Le système 8020</Eyebrow>
              <h2 className="mt-6 max-w-3xl font-heading text-[clamp(1.6rem,4vw,2.6rem)] font-semibold leading-[1.08] tracking-tight">
                De l'attention jusqu'au <span className="text-muted-foreground">chiffre d'affaires.</span>
              </h2>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-100px" }}
              className="relative mt-14 md:mt-20"
            >
              <div aria-hidden className="absolute left-0 right-0 top-[7px] hidden h-px md:block">
                <motion.div
                  variants={lineDraw}
                  className="h-full w-full origin-left bg-gradient-to-r from-primary/60 via-border to-border"
                />
              </div>
              <div aria-hidden className="absolute bottom-0 left-[3px] top-2 w-px md:hidden">
                <motion.div
                  variants={lineDraw}
                  className="h-full w-full origin-top bg-gradient-to-b from-primary/60 via-border to-transparent"
                />
              </div>


              <div className="grid gap-10 md:grid-cols-6 md:gap-6">
                {systemSteps.map((s, i) => (
                  <motion.div
                    key={s.label}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ delay: 0.12 * i, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                    className="relative pl-8 md:pl-0"
                  >
                    <span className="absolute left-0 top-1.5 h-[7px] w-[7px] rounded-full bg-primary md:relative md:left-auto md:top-auto md:mb-8 md:block" />
                    <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-muted-foreground/60">
                      {String(i + 1).padStart(2, "0")}
                    </p>
                    <p className="mt-2 font-heading text-xl tracking-tight text-foreground md:text-2xl">{s.label}</p>
                    <p className="mt-1 text-sm text-muted-foreground/70">{s.note}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>


            <motion.p
              {...reveal}
              className="mt-14 max-w-xl text-lg leading-relaxed text-muted-foreground md:mt-20 md:text-[1.0625rem]"
            >
              Chaque étape influence la suivante.
              <span className="block text-foreground">8020 s'assure qu'elles travaillent comme un seul système.</span>
            </motion.p>
          </div>
        </section>

        {/* 04 — Notre rôle */}
        <section className="border-t border-border/60 px-6 py-20 md:px-8 md:py-28">
          <div className="mx-auto grid max-w-[1280px] gap-14 md:grid-cols-[0.85fr_1.15fr] md:gap-24">
            <motion.div {...reveal}>
              <Eyebrow>Notre rôle</Eyebrow>
              <h2 className="mt-6 font-heading text-[clamp(1.6rem,4vw,2.4rem)] font-semibold leading-[1.08] tracking-tight">
                Votre partenaire
                <span className="block text-muted-foreground">de croissance.</span>
              </h2>
            </motion.div>

            <motion.div {...reveal} className="md:pt-4">
              <p className="max-w-xl text-base leading-relaxed text-muted-foreground md:text-[1.0625rem]">
                Nous intégrons stratégie, contenu, acquisition, conversion et données autour d'un même objectif :{" "}
                <span className="text-foreground">faire croître votre chiffre d'affaires.</span>
              </p>

              <div className="mt-12 md:mt-14">
                {roleLines.map((line, i) => (
                  <motion.div
                    key={line.title}
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ delay: 0.1 * i, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                    className="border-t border-border/70 py-6 last:border-b md:flex md:items-baseline md:gap-10 md:py-8"
                  >
                    <p className="font-heading text-lg tracking-tight text-foreground md:w-64 md:shrink-0 md:text-xl">
                      {line.title}
                    </p>
                    <p className="mt-2 text-base text-muted-foreground md:mt-0 md:text-lg">{line.desc}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* 05 — Nos partenaires */}
        <section className="border-t border-border/60 px-6 py-20 md:px-8 md:py-28">
          <div className="mx-auto max-w-[1280px]">
            <motion.div {...reveal}>
              <Eyebrow>Nos partenaires</Eyebrow>
              <h2 className="mt-6 max-w-4xl font-heading text-[clamp(1.6rem,4vw,2.6rem)] font-semibold leading-[1.08] tracking-tight">
                Nous travaillons avec des entreprises
                <span className="block text-muted-foreground">qui veulent passer à la prochaine étape.</span>
              </h2>
              <p className="mt-8 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-[1.0625rem]">
                8020 accompagne principalement des entreprises avec une offre établie, une ambition de croissance claire
                et la volonté de bâtir une acquisition plus structurée.
              </p>
            </motion.div>

            <div className="mt-14 md:mt-20">
              {partnerCriteria.map((c, i) => (
                <motion.div
                  key={c}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ delay: 0.12 * i, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                  className="flex items-baseline gap-6 border-t border-border/70 py-6 last:border-b md:gap-10 md:py-8"
                >
                  <span className="font-mono text-[11px] tracking-[0.2em] text-primary">0{i + 1}</span>
                  <p className="font-heading text-[clamp(1.25rem,3vw,2rem)] font-medium leading-tight tracking-tight">
                    {c}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* 06 — L'approche */}
        <section className="border-t border-border/60 px-6 py-20 md:px-8 md:py-28">
          <div className="mx-auto max-w-[1280px]">
            <motion.div {...reveal}>
              <Eyebrow>L'approche</Eyebrow>
              <h2 className="mt-6 font-heading text-[clamp(1.6rem,4vw,2.6rem)] font-semibold leading-[1.08] tracking-tight">
                Comprendre. Construire. <span className="text-muted-foreground">Optimiser.</span>
              </h2>
            </motion.div>

            <div className="mt-14 md:mt-20">
              {method.map((m, i) => (
                <motion.div
                  key={m.num}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-70px" }}
                  transition={{ delay: 0.1 * i, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                  className="grid gap-6 border-t border-border/70 py-8 last:border-b md:grid-cols-[auto_1fr_1.2fr] md:items-start md:gap-16 md:py-12"
                >
                  <span className="font-heading text-[clamp(2.2rem,6vw,4rem)] font-semibold leading-none text-muted-foreground/25">
                    {m.num}
                  </span>
                  <p className="font-heading text-xl tracking-tight text-foreground md:pt-4 md:text-2xl">{m.title}</p>
                  <p className="max-w-xl text-base leading-relaxed text-muted-foreground md:pt-5 md:text-lg">
                    {m.desc}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* 07 — FAQ */}
        <section className="border-t border-border/60 px-6 py-20 md:px-8 md:py-28">
          <div className="mx-auto grid max-w-[1280px] gap-14 md:grid-cols-[0.7fr_1.3fr] md:gap-24">
            <motion.div {...reveal}>
              <Eyebrow>Questions</Eyebrow>
              <h2 className="mt-6 font-heading text-[clamp(1.6rem,4vw,2.4rem)] font-semibold leading-[1.08] tracking-tight">
                Avant de
                <span className="block text-muted-foreground">nous parler.</span>
              </h2>
            </motion.div>

            <motion.div {...reveal}>
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((f, i) => (
                  <AccordionItem key={f.q} value={`faq-${i}`} className="border-b border-border/70">
                    <AccordionTrigger className="py-5 text-left font-heading text-base tracking-tight hover:no-underline md:text-lg">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="pb-6 text-base leading-relaxed text-muted-foreground">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </motion.div>
          </div>
        </section>

        {/* 08 — CTA final */}
        <section className="border-t border-border/60 px-6 py-20 md:px-8 md:py-28">
          <motion.div {...reveal} className="mx-auto max-w-[1280px]">
            <Eyebrow>Travaillons ensemble</Eyebrow>
            <h2 className="mt-6 max-w-4xl font-heading text-[clamp(1.7rem,4.4vw,2.9rem)] font-semibold leading-[1.05] tracking-tight">
              Votre prochaine phase de croissance
              <span className="block text-muted-foreground">commence peut-être ici.</span>
            </h2>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-[1.0625rem]">
              Parlez-nous de votre entreprise, de vos objectifs et de ce que vous cherchez à bâtir.
              <span className="mt-3 block text-foreground">
                Si nous croyons pouvoir avoir un réel impact, nous poursuivrons la conversation.
              </span>
            </p>
            <Button
              onClick={openForm}
              className="group mt-10 h-auto rounded-full bg-primary px-8 py-5 text-base font-semibold text-primary-foreground hover:bg-primary/90"
            >
              Présenter mon entreprise
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
            <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground/60">
              Nombre de partenaires limité · Réponse sous 48 h · Aucun engagement à cette étape
            </p>
          </motion.div>
        </section>

        <footer className="border-t border-border/60 px-6 py-10 md:px-8">
          <div className="mx-auto max-w-[1280px] text-xs text-muted-foreground/60">
            © {new Date().getFullYear()} Agence 80/20. Tous droits réservés.
          </div>
        </footer>
      </div>
    </>
  );
};

export default Accompagnement;
